import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { CheckCircle, Clock3, XCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const transactionId = searchParams.get('transaction_id');
  const { refreshUser, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState('checking');
  const [creditsAdded, setCreditsAdded] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let retryTimer;
    let attempts = 0;
    const verifyPayment = async () => {
      if (!transactionId) {
        setState('failed');
        return;
      }
      try {
        const response = await axios.get(`${API}/payments/status/${transactionId}`, {
          headers: getAuthHeaders(),
          withCredentials: true,
        });
        if (cancelled) return;
        if (response.data?.payment_status === 'succeeded') {
          setCreditsAdded(response.data?.credits_added || 0);
          await refreshUser();
          if (!cancelled) setState('success');
        } else if (['failed', 'cancelled'].includes(response.data?.payment_status)) {
          setState('failed');
        } else {
          setState('pending');
          attempts += 1;
          if (attempts < 20) retryTimer = window.setTimeout(verifyPayment, 3000);
        }
      } catch {
        if (!cancelled) setState('unavailable');
      }
    };
    verifyPayment();
    return () => {
      cancelled = true;
      window.clearTimeout(retryTimer);
    };
  }, [transactionId, getAuthHeaders, refreshUser]);

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#050505' }}>
      <div className="glass-card rounded-xl p-12 text-center max-w-md" data-testid="payment-result">
        {state === 'success' ? (
          <>
            <CheckCircle className="w-16 h-16 mx-auto mb-4" style={{ color: '#10B981' }} />
            <h2 className="text-xl font-bold mb-2" style={{ fontFamily: 'Syne' }}>Payment confirmed</h2>
            <p className="text-sm mb-6" style={{ color: '#94A3B8' }}>
              {creditsAdded > 0 ? `${creditsAdded} simulation credit${creditsAdded === 1 ? '' : 's'} added to your account.` : 'Your credits are ready to use.'}
            </p>
          </>
        ) : state === 'checking' || state === 'pending' ? (
          <>
            <Clock3 className="w-16 h-16 mx-auto mb-4" style={{ color: '#F59E0B' }} />
            <h2 className="text-xl font-bold mb-2" style={{ fontFamily: 'Syne' }}>Confirming payment</h2>
            <p className="text-sm mb-6" style={{ color: '#94A3B8' }}>The payment is still processing. Your credits will appear after Dodo Payments confirms it.</p>
          </>
        ) : state === 'failed' ? (
          <>
            <XCircle className="w-16 h-16 mx-auto mb-4" style={{ color: '#EF4444' }} />
            <h2 className="text-xl font-bold mb-2" style={{ fontFamily: 'Syne' }}>Payment not confirmed</h2>
            <p className="text-sm mb-6" style={{ color: '#94A3B8' }}>No credits were added. Please try again or contact ritik@solvailabs.com if you were charged.</p>
          </>
        ) : (
          <>
            <Clock3 className="w-16 h-16 mx-auto mb-4" style={{ color: '#F59E0B' }} />
            <h2 className="text-xl font-bold mb-2" style={{ fontFamily: 'Syne' }}>Verification is temporarily unavailable</h2>
            <p className="text-sm mb-6" style={{ color: '#94A3B8' }}>Dodo Payments may still add your credits through the secure webhook. Check your dashboard shortly; contact ritik@solvailabs.com if you were charged and the credits do not appear.</p>
          </>
        )}
        <div className="flex gap-3 justify-center">
          <Button data-testid="go-to-dashboard-after-payment" onClick={() => navigate('/dashboard')} className="rounded-full px-8 py-4" style={{ background: '#6366F1' }}>
            Go to Dashboard
          </Button>
          {state === 'failed' && (
            <Button data-testid="try-again-payment" onClick={() => navigate('/upgrade')} variant="outline" className="rounded-full px-6 py-4 border-white/10">
              Try Again
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
